import { useEffect, useState } from "react";
import { Crown, Clock, AlertCircle, XCircle, Zap, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

function InlinePlanPicker({ ctaLabel = "Choose" }) {
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);
  if (plans.length === 0) return null;
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="inline-plan-picker">
      {plans.map((p) => {
        const popular = p.plan_id === "advanced";
        const pro = p.plan_id === "pro";
        return (
          <div
            key={p.plan_id}
            data-testid={`inline-plan-${p.plan_id}`}
            className={`relative rounded-xl border bg-white p-4 ${popular ? "border-teal-500 ring-2 ring-teal-100" : "border-slate-200"}`}
          >
            {popular && (
              <span className="absolute -top-2 left-3 bg-teal-600 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">POPULAR</span>
            )}
            {pro && (
              <span className="absolute -top-2 right-3 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                <Check className="w-2.5 h-2.5" /> VERIFIED
              </span>
            )}
            <div className="font-heading font-bold text-sm uppercase tracking-wide text-slate-800">{p.name}</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-heading text-2xl font-extrabold text-slate-900">₹{p.price}</span>
              <span className="text-[11px] text-slate-500">/ {p.billing_cycle}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{p.duration_days} days</div>
            <Button
              data-testid={`inline-choose-${p.plan_id}`}
              onClick={() => navigate(`/payment/${p.plan_id}`)}
              className={`mt-3 w-full h-8 rounded-full text-xs font-semibold ${popular ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}
            >
              {ctaLabel}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export default function SubscriptionStatusCard({ status }) {
  const navigate = useNavigate();
  if (!status) return null;
  const s = status.state;

  if (s === "ACTIVE") {
    const sub = status.subscription;
    return (
      <div data-testid="sub-card-active" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-emerald-700">
              <Crown className="w-4 h-4" /> Active Subscription
            </div>
            <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{sub.plan_name}</div>
            <div className="text-sm text-slate-600 mt-1">
              Started {sub.start_date ? new Date(sub.start_date).toLocaleDateString() : "—"} · Expires {sub.expiry_date ? new Date(sub.expiry_date).toLocaleDateString() : "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Remaining</div>
            <div className="font-heading text-3xl font-extrabold text-emerald-700">{status.remaining_days}</div>
            <div className="text-xs text-slate-500">days</div>
          </div>
        </div>
      </div>
    );
  }

  if (s === "PAYMENT_PENDING") {
    return (
      <div data-testid="sub-card-pending" className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
        <div className="flex items-center gap-3">
          <Clock className="w-7 h-7 text-amber-600" />
          <div>
            <div className="font-heading font-extrabold text-lg text-slate-900">Payment Verification Pending</div>
            <div className="text-sm text-slate-600">Your {status.subscription?.plan_name} payment is awaiting admin verification. You'll get access as soon as it's approved.</div>
          </div>
        </div>
      </div>
    );
  }

  if (s === "EXPIRED") {
    return (
      <div data-testid="sub-card-expired" className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-7 h-7 text-slate-500" />
          <div>
            <div className="font-heading font-extrabold text-lg text-slate-900">Subscription Expired</div>
            <div className="text-sm text-slate-600">Your {status.subscription?.plan_name} has ended. Pick a plan below to renew — one tap away.</div>
          </div>
        </div>
        <InlinePlanPicker ctaLabel="Renew" />
      </div>
    );
  }

  if (s === "REJECTED") {
    return (
      <div data-testid="sub-card-rejected" className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-5">
        <div className="flex items-start gap-3">
          <XCircle className="w-7 h-7 text-rose-600 shrink-0" />
          <div>
            <div className="font-heading font-extrabold text-lg text-slate-900">Payment Rejected</div>
            <div className="text-sm text-slate-600">{status.rejection_reason || "Admin rejected your last payment."} You can submit a new payment.</div>
          </div>
        </div>
        <InlinePlanPicker ctaLabel="Try again" />
      </div>
    );
  }

  return (
    <div data-testid="sub-card-none" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-teal-50 to-amber-50 p-5">
      <div className="flex items-center gap-3">
        <Zap className="w-7 h-7 text-amber-600" />
        <div>
          <div className="font-heading font-extrabold text-lg text-slate-900">Unlock MyLiveFriend</div>
          <div className="text-sm text-slate-600">Pick a plan to view full profiles and chat with verified neighbours.</div>
        </div>
      </div>
      <InlinePlanPicker ctaLabel="Choose" />
    </div>
  );
}
